import { requestErrorNotification } from '@gonfalon/error-boundaries/requestErrorNotification';
import { msg } from '@gonfalon/messages/msg';
import type { Metric } from '@gonfalon/metrics/types';
import { postMetric } from '@gonfalon/openapi/operations/postMetric';
import { getMetricsQuery, metricsList } from '@gonfalon/rest-api/queries/metrics';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import type { MetricPost } from './formUtils';

const errorBodySchema = z.object({ message: z.string().min(1) });

export function useCreateMetric() {
  const queryClient = useQueryClient();
  const projectKey = useProjectKey();

  return useMutation({
    mutationFn: (metric: MetricPost) => createMetric(metric, projectKey),
    onError: requestErrorNotification(msg('metrics.createModal.createError')),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: metricsList({ projectKey }).queryKey }),
        queryClient.invalidateQueries({ queryKey: getMetricsQuery.partialQueryKey({ projectKey }) }),
      ]);
    },
  });
}

async function createMetric(metric: MetricPost, projectKey: string): Promise<Metric> {
  const { data, error, response } = await postMetric({ projectKey, body: metric, apiVersion: 'beta' });
  if (!response.ok) {
    const parsed = errorBodySchema.safeParse(error);
    throw new Error(parsed.success ? parsed.data.message : response.statusText || 'Unknown error');
  }
  return data as Metric;
}
